import { useEffect, useRef, useState } from 'react';
import type { GCodeFile, RunState } from '@/types/machine.types';
import { AXIS_CONFIG, AXIS_INDEX } from '@/types/machine.types';
import gcodeRunImg from '@/assets/gcode-run.webp';
import {
  ArrowIcon,
  BackIcon,
  CloseIcon,
  CodeIcon,
  DimensionIcon,
  EstimatedTimesIcon,
  PauseIcon,
  ProcessingIcon,
  RunIcon,
  StopIcon,
  ToolT03Icon,
  WorkOffsetIcon,
} from '@/icons';
import styles from '@/components/GCodePreviewModal/GCodePreviewModal.module.css';
import { Selector } from '@/components/jog/Selector';
import {
  FEED_OVERRIDE_OPTIONS,
  FEED_PERCENTS,
  nearestIndex,
  SPINDLE_OVERRIDE_OPTIONS,
  SPINDLE_PERCENTS,
} from '@/components/jog/overrideOptions';
import {
  useActiveTool,
  useFeedOverride,
  useFeedSpindle,
  useGcodePreviewWindow,
  usePosition,
  useSendCommand,
  useSpindleOn,
  useSpindleOverride,
  useWorkOffset,
  useWorkPosition,
} from '@/store/store';
import { truncateFilename } from '@/lib/truncateFilename';

// Snap sub-micron values to a clean "0.000" so float noise / negative zero never shows.
function formatDro(value: number): string {
  return (Math.abs(value) < 0.001 ? 0 : value).toFixed(3);
}

// F/S read-back: round off floating-point noise (1200 rather than 1200.0000001).
function formatFs(value: number): string {
  return String(Math.round(value * 1000) / 1000);
}

/** Program metadata shown in the preview modal — real values will come from backend. */
export interface ProgramMetadata {
  readonly dimension: { x: number; y: number; z: number };
  readonly estimatedTime: string;
}

interface GCodePreviewModalProps {
  readonly gcodeFile: GCodeFile;
  readonly metadata: ProgramMetadata;
  readonly runState: RunState;
  readonly progressPercent: number;
  /** 0-based index of the currently-executing line in `modifiedLines` (-1 = idle). */
  readonly motionLine: number;
  readonly onBack: () => void;
  readonly onSingleBlock: () => void;
  readonly onRun: () => void;
  readonly onPause: () => void;
  readonly onResume: () => void;
  readonly onStop: () => void;
  readonly onClose: () => void;
}

function InfoCards({ metadata }: { metadata: ProgramMetadata }): JSX.Element {
  const { dimension, estimatedTime } = metadata;
  const { name: workOffsetName, offset: workOffset } = useWorkOffset();
  return (
    <div className={styles.previewModalInfo}>
      <div className={styles.previewModalCard}>
        <div className={styles.previewModalCardHead}>
          <DimensionIcon width={16} height={16} />
          Dimension
        </div>
        <div className={styles.previewModalCardVals}>
          <span className={styles.previewModalDimAxis}>
            <i>X</i>
            <span>
              {dimension.x.toFixed(2)}
              <small>mm</small>
            </span>
          </span>
          <span className={styles.previewModalDimAxis}>
            <i>Y</i>
            <span>
              {dimension.y.toFixed(2)}
              <small>mm</small>
            </span>
          </span>
          <span className={styles.previewModalDimAxis}>
            <i>Z</i>
            <span>
              {dimension.z.toFixed(2)}
              <small>mm</small>
            </span>
          </span>
        </div>
      </div>

      <div className={styles.previewModalCard}>
        <div className={styles.previewModalCardHead}>
          <EstimatedTimesIcon width={16} height={16} />
          Estimated Times
        </div>
        <div className={`${styles.previewModalCardVals} ${styles.previewModalCardValsSingle}`}>
          <b>{estimatedTime}</b>
          <small>hh:mm:ss</small>
        </div>
      </div>

      <div className={styles.previewModalCard}>
        <div className={styles.previewModalCardHead}>
          <WorkOffsetIcon width={16} height={16} />
          Work Offset
        </div>
        <div className={`${styles.previewModalCardVals} ${styles.previewModalCardValsOffset}`}>
          <b>{workOffsetName}</b>
          <span>
            X{(workOffset[AXIS_INDEX.X] ?? 0).toFixed(2)} Y
            {(workOffset[AXIS_INDEX.Y] ?? 0).toFixed(2)} Z
            {(workOffset[AXIS_INDEX.Z] ?? 0).toFixed(2)}
          </span>
        </div>
      </div>
    </div>
  );
}

/** Live axis positions (DRO) + the effective F/S on the machine so the operator
 *  can watch the job run. The backend reports the programmed F/S (G-code modal
 *  values). The real feed/speed at the motors is that programmed value scaled by
 *  the override the operator sets from the UI, so apply the override here —
 *  otherwise the readout stays at the G-code value and ignores the override. */
function LiveAxes(): JSX.Element {
  const position = usePosition();
  const workPosition = useWorkPosition();
  const { feedRate, spindleSpeed } = useFeedSpindle();
  const feedOverride = useFeedOverride();
  const spindleOverride = useSpindleOverride();
  const spindleOn = useSpindleOn();
  const effectiveFeedRate = (feedRate * feedOverride) / 100;
  // spindleSpeed is the commanded value and persists after M5. Show 0 when the
  // spindle is off so the readout matches the real state, not the last command.
  const effectiveSpindleSpeed = spindleOn ? (spindleSpeed * spindleOverride) / 100 : 0;
  return (
    <div className={styles.previewModalDro}>
      <div className={styles.previewModalAxes}>
        {AXIS_CONFIG.map((axis) => {
          const rawPos = position[axis.index];
          const rawWork = workPosition[axis.index];
          const machine = typeof rawPos === 'number' ? rawPos : 0;
          const work = typeof rawWork === 'number' ? rawWork : 0;
          return (
            <div key={axis.name} className={styles.previewModalAxis}>
              <span className={styles.previewModalAxisCap}>{axis.name}</span>
              <div className={styles.previewModalAxisVals}>
                <span className={styles.previewModalAxisPrimary}>{formatDro(work)}</span>
                <span className={styles.previewModalAxisSecondary}>{formatDro(machine)}</span>
              </div>
            </div>
          );
        })}
      </div>
      <div className={styles.previewModalFs}>
        <div className={styles.previewModalFsItem}>
          <span className={styles.previewModalFsCap}>Feed rate</span>
          <div className={styles.previewModalFsVals}>
            <span className={styles.previewModalFsPrimary}>{formatFs(effectiveFeedRate)}</span>
            <span className={styles.previewModalFsUnit}>/min</span>
          </div>
        </div>
        <div className={styles.previewModalFsItem}>
          <span className={styles.previewModalFsCap}>Spindle speed</span>
          <div className={styles.previewModalFsVals}>
            <span className={styles.previewModalFsPrimary}>{formatFs(effectiveSpindleSpeed)}</span>
            <span className={styles.previewModalFsUnit}>rpm</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ToolCard(): JSX.Element {
  // Reflect the tool currently in the spindle (machine-reported via STATUS,
  // resolved against the tool table). Empty spindle → dash, no spec.
  const activeTool = useActiveTool();
  return (
    <div className={styles.previewModalTool}>
      <div className={styles.previewModalCardHead}>
        <ToolT03Icon width={40} height={40} />
        Tool
      </div>
      <span className={styles.previewModalToolNumber}>{activeTool?.no ?? '—'}</span>
      <span className={styles.previewModalToolSpec}>
        {activeTool ? `${activeTool.type} ${activeTool.diameter}` : ''}
      </span>
    </div>
  );
}

// Fixed-row virtualization geometry. A G-code file can be thousands of lines;
// rendering every row (and re-rendering the whole list at 20Hz as motionLine
// advances) froze the Pi. Only the visible window (+overscan) is mounted, so
// cost is bounded regardless of program size.
//
// Fixed-size virtualization needs the row pitch as a number in JS to map
// scrollTop → visible range (the same reason react-window takes `itemSize`).
// These constants are the single source of truth: the layout below is applied
// to the DOM via inline styles from these values, so the CSS carries no
// duplicated pixel numbers to drift out of sync.
const ROW_HEIGHT = 26;
const ROW_GAP = 8;
const ROW_STRIDE = ROW_HEIGHT + ROW_GAP; // one row's top to the next
const LIST_PAD_TOP = 12;
const LIST_PAD_BOTTOM = 10;
const VISIBLE_ROWS = 5; // rows shown before the list scrolls (design spec)
// Height of the scroll viewport: VISIBLE_ROWS rows (last has no trailing gap)
// plus the list's own top/bottom padding.
const LIST_MAX_HEIGHT = VISIBLE_ROWS * ROW_STRIDE - ROW_GAP + LIST_PAD_TOP + LIST_PAD_BOTTOM;

function GCodePreviewList({
  lines,
  motionLine,
  runState,
}: {
  lines: readonly string[];
  motionLine: number;
  runState: RunState;
}): JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  // Dev-tunable count of rows kept mounted (the virtualization window). Default
  // 17; a dev setting raises it (50/100/150/200) to stress-test Pi render cost.
  const previewWindow = useGcodePreviewWindow();
  // Coalesce scroll events to one state update per frame. Raw onScroll fires
  // faster than the Pi can re-render the list, which drops frames. rafRef holds
  // the pending frame; lastTopRef holds the latest scrollTop so the frame uses
  // the newest value, not the first of the burst.
  const rafRef = useRef(0);
  const lastTopRef = useRef(0);
  // Auto-follow the running line, but yield to the operator: once they scroll
  // by hand, stop re-pinning so the view stays where they left it. Follow
  // re-arms when a run (re)starts — see the runState effect below.
  const followRef = useRef(true);
  // The scrollTop we set ourselves, so onScroll can tell our scroll from the
  // operator's — a hand scroll lands elsewhere and pauses follow.
  const programmaticTopRef = useRef(-1);
  // Previous runState, to detect the transition into 'running' (start, repeat
  // run, or resume) that re-arms follow after a manual scroll.
  const prevRunStateRef = useRef<RunState>(runState);

  useEffect(
    () => () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    },
    [],
  );

  // Entering 'running' (idle/done/paused -> running) re-arms follow so a fresh
  // run or a resume starts tracking again, even after the operator scrolled.
  useEffect(() => {
    if (runState === 'running' && prevRunStateRef.current !== 'running') {
      followRef.current = true;
    }
    prevRunStateRef.current = runState;
  }, [runState]);

  // Follow execution: pin the currently-running line near the top of the list
  // so the lines *about to be machined* are visible below it (operators care
  // about what's coming, not what's already done). One already-run line is kept
  // above for context. With virtualization the active row may not be mounted,
  // so scroll by computed offset instead of scrollIntoView on a ref.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || motionLine < 0 || !followRef.current) return;
    const CONTEXT_ROWS_ABOVE = 1;
    const target = Math.max(0, motionLine - CONTEXT_ROWS_ABOVE) * ROW_STRIDE;
    if (el.scrollTop === target) return; // already pinned — don't fight the view
    programmaticTopRef.current = target;
    el.scrollTop = target;
  }, [motionLine]);

  const total = lines.length;
  // Derive the overscan from the target window so VISIBLE_ROWS (the viewport
  // height) stays fixed and exactly `previewWindow` rows mount (bar end-clamping).
  const overscan = Math.max(0, Math.floor((previewWindow - VISIBLE_ROWS) / 2));
  const firstVisible = Math.max(0, Math.floor((scrollTop - LIST_PAD_TOP) / ROW_STRIDE) - overscan);
  const lastVisible = Math.min(total, firstVisible + previewWindow);
  const sizerHeight =
    LIST_PAD_TOP + LIST_PAD_BOTTOM + (total > 0 ? total * ROW_STRIDE - ROW_GAP : 0);

  const rows: JSX.Element[] = [];
  for (let i = firstVisible; i < lastVisible; i++) {
    const active = i === motionLine;
    rows.push(
      <div
        key={i}
        className={`${styles.previewModalGcodeRow} ${active ? styles.previewModalGcodeRowActive : ''}`}
        style={{
          position: 'absolute',
          top: LIST_PAD_TOP + i * ROW_STRIDE,
          left: 0,
          right: 0,
          height: ROW_HEIGHT,
          lineHeight: `${ROW_HEIGHT}px`,
          boxSizing: 'border-box',
        }}
      >
        <span className={styles.previewModalGcodeGutter}>{i + 1}</span>
        <span className={styles.previewModalGcodeCode}>{lines[i] ?? ''}</span>
      </div>,
    );
  }

  return (
    <div className={styles.previewModalGcode}>
      <div className={styles.previewModalGcodeHead}>
        <span className={styles.previewModalGcodeTitle}>
          <CodeIcon width={24} height={15} />
          G-Code Preview
        </span>
        <span className={styles.previewModalGcodeCount}>{lines.length} Lines</span>
      </div>
      <div
        className={styles.previewModalGcodeList}
        ref={scrollRef}
        style={{ maxHeight: LIST_MAX_HEIGHT }}
        onScroll={(e) => {
          const top = e.currentTarget.scrollTop;
          // A scroll that didn't land on our pin target is the operator's —
          // pause follow so we stop yanking the list back under them.
          if (Math.abs(top - programmaticTopRef.current) > 1) followRef.current = false;
          lastTopRef.current = top;
          if (rafRef.current) return;
          rafRef.current = requestAnimationFrame(() => {
            rafRef.current = 0;
            setScrollTop(lastTopRef.current);
          });
        }}
      >
        <div className={styles.previewModalGcodeSizer} style={{ height: sizerHeight }}>
          {rows}
        </div>
      </div>
    </div>
  );
}

/** Feed + Spindle override carousels — live-adjustable before and during a run. */
function ProgramOverrides(): JSX.Element {
  const sendCommand = useSendCommand();
  const feedOverride = useFeedOverride();
  const spindleOverride = useSpindleOverride();

  const feedIndex = nearestIndex(FEED_PERCENTS, feedOverride);
  const spindleIndex = nearestIndex(SPINDLE_PERCENTS, spindleOverride);

  return (
    <div className={styles.previewModalOverrides}>
      <div className={styles.previewModalOverrideCard}>
        <Selector
          label="Feed Override"
          options={FEED_OVERRIDE_OPTIONS}
          activeIndex={feedIndex}
          onChange={(i) => sendCommand({ type: 'FEED_OVERRIDE', percent: FEED_PERCENTS[i] ?? 100 })}
          windowSize={5}
        />
      </div>
      <div className={styles.previewModalOverrideCard}>
        <Selector
          label="Spindle Override"
          options={SPINDLE_OVERRIDE_OPTIONS}
          activeIndex={spindleIndex}
          onChange={(i) =>
            sendCommand({ type: 'SPINDLE_OVERRIDE', percent: SPINDLE_PERCENTS[i] ?? 100 })
          }
          windowSize={5}
        />
      </div>
    </div>
  );
}

export function GCodePreviewModal({
  gcodeFile,
  metadata,
  runState,
  progressPercent,
  motionLine,
  onBack,
  onSingleBlock,
  onRun,
  onPause,
  onResume,
  onStop,
  onClose,
}: GCodePreviewModalProps): JSX.Element {
  const isProcessing = runState === 'running' || runState === 'paused';

  return (
    <div
      className={styles.previewModalOverlay}
      role="dialog"
      aria-modal="true"
      aria-label="G-Code Preview"
    >
      <div className={styles.previewModal}>
        {/* Header — preview vs processing */}
        {isProcessing ? (
          <div className={`${styles.previewModalHeader} ${styles.previewModalHeaderProcessing}`}>
            <span className={styles.previewModalTitle}>
              <ProcessingIcon width={18} height={18} />
              Processing…
            </span>
            {runState === 'paused' && (
              <button
                className={styles.previewModalClose}
                onClick={onClose}
                type="button"
                aria-label="Close"
              >
                <CloseIcon width={20} height={20} />
              </button>
            )}
          </div>
        ) : (
          <div className={styles.previewModalHeader}>
            <button
              className={styles.previewModalBackIcon}
              onClick={onBack}
              type="button"
              aria-label="Back"
            >
              <ArrowIcon width={22} height={22} />
            </button>
            <span className={styles.previewModalPath} title={gcodeFile.path}>
              {truncateFilename(gcodeFile.path)}
            </span>
          </div>
        )}

        {/* 3D preview viewport */}
        <div className={styles.previewModalViewport}>
          <img src={gcodeRunImg} alt="Program preview" />
        </div>

        {/* Scrollable body — header + viewport stay pinned above, actions below. */}
        <div className={styles.previewModalBody}>
          {/* Live axis positions (DRO) — only while a job is running/paused */}
          {isProcessing && <LiveAxes />}

          {/* Info cards */}
          <InfoCards metadata={metadata} />

          {/* Tool + G-Code preview */}
          <div className={styles.previewModalLower}>
            <ToolCard />
            <GCodePreviewList
              lines={gcodeFile.modifiedLines}
              motionLine={motionLine}
              runState={runState}
            />
          </div>

          {/* Progress (processing only) */}
          {isProcessing && (
            <div className={styles.previewModalProgressWrap}>
              <div
                className={`${styles.previewModalProgress} ${runState === 'paused' ? styles.previewModalProgressPaused : ''}`}
              >
                <div
                  className={styles.previewModalProgressBar}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span className={styles.previewModalProgressText}>
                {runState === 'paused' ? 'Paused…' : 'Running…'}
              </span>
            </div>
          )}

          {/* Feed + Spindle overrides — adjustable before and during a run */}
          <ProgramOverrides />
        </div>

        {isProcessing ? (
          <div className={styles.previewModalActions}>
            {runState === 'paused' ? (
              <button
                className={`${styles.previewModalBtn} ${styles.previewModalBtnPause}`}
                onClick={onResume}
                type="button"
              >
                <RunIcon width={16} height={16} />
                Resume
              </button>
            ) : (
              <button
                className={`${styles.previewModalBtn} ${styles.previewModalBtnPause}`}
                onClick={onPause}
                type="button"
              >
                <PauseIcon width={16} height={16} />
                Pause
              </button>
            )}
            <button
              className={`${styles.previewModalBtn} ${styles.previewModalBtnStop}`}
              onClick={onStop}
              type="button"
            >
              <StopIcon width={16} height={16} />
              Stop
            </button>
          </div>
        ) : (
          <div className={styles.previewModalActions}>
            <button
              className={`${styles.previewModalBtn} ${styles.previewModalBtnBack}`}
              onClick={onBack}
              type="button"
            >
              <BackIcon width={16} height={16} />
              Back
            </button>
            <button
              className={`${styles.previewModalBtn} ${styles.previewModalBtnReplace}`}
              onClick={onSingleBlock}
              type="button"
            >
              Single Block
            </button>
            <button
              className={`${styles.previewModalBtn} ${styles.previewModalBtnRun}`}
              onClick={onRun}
              type="button"
            >
              <RunIcon width={16} height={16} />
              Run
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
